"use client";

import Link from "next/link";
import { AuthLocaleRoot } from "@/components/i18n/auth-locale-root";
import { SkipToMain } from "@/components/accessibility/skip-to-main";
import { SiteHeader } from "@/components/marketing/site-header";
import { SiteFooter } from "@/components/marketing/site-footer";
import { SUPPORT_EMAIL } from "@/lib/support-email";

const LAST_UPDATED = "June 16, 2026";

function AccessibilityContent() {
  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-900 antialiased">
      <SkipToMain />
      <SiteHeader variant="default" />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
        <nav className="mb-8 text-sm text-slate-600">
          <Link href="/" className="hover:text-slate-900 hover:underline">← Home</Link>
        </nav>

        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Accessibility statement</h1>
        <p className="mt-2 text-sm text-slate-600">Last updated: {LAST_UPDATED}</p>

        <div className="mt-8 space-y-8 text-sm leading-relaxed text-slate-700">
          <section>
            <h2 className="text-lg font-semibold text-slate-900">Our commitment</h2>
            <p className="mt-2">
              Jorvio Quotes is committed to making our website and customer-facing quote experience accessible to people
              with disabilities. We aim to conform to the{" "}
              <a
                href="https://www.w3.org/WAI/standards-guidelines/wcag/"
                className="font-medium text-primary underline-offset-4 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Web Content Accessibility Guidelines (WCAG) 2.1
              </a>{" "}
              Level AA where practicable.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">Measures we take</h2>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>Keyboard focus styles and skip links on major pages</li>
              <li>Semantic landmarks (<code className="text-xs">header</code>, <code className="text-xs">nav</code>, <code className="text-xs">main</code>)</li>
              <li>Visible labels and descriptions on auth, settings, and public quote flows</li>
              <li>Page language (<code className="text-xs">lang</code>) synced when locale changes in the app</li>
              <li>Reduced motion support via <code className="text-xs">prefers-reduced-motion</code></li>
              <li>Periodic automated accessibility checks on critical public URLs</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">Known limitations</h2>
            <ul className="mt-2 list-disc space-y-2 pl-5">
              <li>
                <strong>Signature pad:</strong> Customers draw signatures with a pointer or finger. Full keyboard-only
                signing is not supported; instructions are provided at the pad.
              </li>
              <li>
                <strong>Plan markup:</strong> Some drawing overlays are pointer-based; contractors can describe work in
                text fields where provided.
              </li>
              <li>
                <strong>Third-party checkout:</strong> Stripe-hosted payment pages are operated by Stripe and subject to
                Stripe&apos;s accessibility practices.
              </li>
              <li>
                <strong>Contractor workspace:</strong> The quote editor is large and evolving; we prioritize
                customer-facing and authentication flows first.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">Feedback and assistance</h2>
            <p className="mt-2">
              If you have difficulty using Jorvio Quotes or need content in an alternative format, please contact us. We
              take accessibility feedback seriously and will work with you to provide the information or service through
              an alternative communication method when possible.
            </p>
            <ul className="mt-3 space-y-2">
              <li>
                Email:{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-primary underline-offset-4 hover:underline">
                  {SUPPORT_EMAIL}
                </a>
              </li>
              <li>
                Contact form:{" "}
                <Link href="/contact" className="font-medium text-primary underline-offset-4 hover:underline">
                  jorvioquotes.com/contact
                </Link>
              </li>
            </ul>
            <p className="mt-3 text-slate-600">
              Please include the page URL, a short description of the issue, and your preferred contact method. We
              typically respond within several business days.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-slate-900">Related policies</h2>
            <ul className="mt-2 space-y-2">
              <li>
                <Link href="/legal/privacy" className="font-medium text-primary underline-offset-4 hover:underline">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href="/legal/terms" className="font-medium text-primary underline-offset-4 hover:underline">
                  Terms of Service
                </Link>
              </li>
            </ul>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

export function MarketingAccessibilityPage() {
  return (
    <AuthLocaleRoot>
      <AccessibilityContent />
    </AuthLocaleRoot>
  );
}
